import { IfAbility } from '@efdigital/ll-addon-auth';
import type { Coaching } from '@efdigital/ll-addon-data/types/coaching/coaching';
import type {
  CoachingPartnership,
  CoachingPartnershipStatus,
} from '@efdigital/ll-addon-data/types/coaching/coaching-partnership';
import type { CoachingPlan } from '@efdigital/ll-addon-data/types/coaching/coaching-plan';
import { BestMatchedCoaches } from '@efdigital/ll-admin-v2/components/coaching/best-matched-coaches.gts';
import { CoachingPartnershipSessions } from '@efdigital/ll-admin-v2/components/coaching/coaching-partnership-sessions.gts';
import { ReassignCoachModal } from '@efdigital/ll-admin-v2/components/coaching/reassign-coach-modal.gts';
import { findCoachingPartnershipsByCoachingPlan } from '@efdigital/ll-admin-v2/data/builders/coaching/coaching-partnerships';
import eq from '@efdigital/ll-admin-v2/helpers/eq.ts';
import type UserLookupService from '@efdigital/ll-admin-v2/services/user-lookup';
import { resolveCoachNames } from '@efdigital/ll-admin-v2/utils/coaching/coach-display-name';
import { userDisplayName } from '@efdigital/ll-admin-v2/utils/users';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';
import { Await } from '@warp-drive/ember';

const STATUS_CLASS: Record<string, string> = {
  pending: 'text-bg-secondary',
  chemistry: 'text-bg-info',
  declined: 'text-bg-warning',
  chosen: 'text-bg-success',
  reassigned: 'text-bg-secondary',
};

function partnershipStatusClass(status: CoachingPartnershipStatus): string {
  return STATUS_CLASS[status] ?? 'text-bg-secondary';
}

function chosenPartnership(
  partnerships: CoachingPartnership[]
): CoachingPartnership | undefined {
  return partnerships.find((partnership) => partnership.status === 'chosen');
}

interface CoachingPlanOversightSignature {
  Args: {
    plan: CoachingPlan;
    /** The cohort's coaching record — supplies the coach pool for reassignment. */
    coaching: Coaching;
    cohortId: string;
  };
  Element: HTMLDivElement;
}

/**
 * Oversight for a single coaching plan (coachee): a collapsed row that expands
 * to partnerships, session history, and the same ranked coach list the coachee
 * sees. Partnerships and matches load lazily on expand.
 */
export class CoachingPlanOversight extends Component<CoachingPlanOversightSignature> {
  @service declare store: Store;
  @service declare userLookup: UserLookupService;

  @tracked expanded = false;
  @tracked coachNames: Record<string, string> = {};
  /** Bumped to re-run the partnerships and matches loads after a reassignment. */
  @tracked reloadToken = 0;
  /** Pre-selects this coach when Assign opens the shared modal. */
  @tracked assignCoachUserId = '';

  toggle = () => {
    this.expanded = !this.expanded;
  };

  loadCoachee = async () => {
    await this.userLookup.lookupUsers([this.args.plan.user]);
    return this.userLookup.fromCache(this.args.plan.user);
  };

  loadPartnerships = async (): Promise<CoachingPartnership[]> => {
    // Reading the token is what makes this re-run after a reassignment; a
    // non-zero token also means the cached rows are stale, so bypass them.
    const hasReassigned = this.reloadToken > 0;
    const result = await this.store.request(
      findCoachingPartnershipsByCoachingPlan(this.args.plan.id!, {
        reload: hasReassigned,
      })
    );
    const partnerships = result.content.data ?? [];
    if (partnerships.length) {
      this.coachNames = await resolveCoachNames(
        this.store,
        this.userLookup,
        partnerships.map((partnership) => partnership.coachUserId)
      );
    }
    return partnerships;
  };

  /**
   * Forces the partnerships and matches Awaits to re-run after a reassignment.
   */
  reloadPartnerships = (): Promise<void> => {
    this.reloadToken += 1;
    this.assignCoachUserId = '';
    return Promise.resolve();
  };

  /** Persisted plans always have an id; the partnerships load already assumes it. */
  get planId(): string {
    return this.args.plan.id!;
  }

  coachNameFor = (coachUserId: string): string => {
    return this.coachNames[coachUserId] ?? coachUserId;
  };

  currentCoachUserIdOf = (partnerships: CoachingPartnership[]): string =>
    chosenPartnership(partnerships)?.coachUserId ?? '';

  currentCoachNameOf = (partnerships: CoachingPartnership[]): string => {
    const chosen = chosenPartnership(partnerships);
    return chosen ? this.coachNameFor(chosen.coachUserId) : '';
  };

  partnershipIdOf = (partnerships: CoachingPartnership[]): string =>
    chosenPartnership(partnerships)?.id ?? '';

  openReassign = (showModal: () => void) => {
    this.assignCoachUserId = '';
    showModal();
  };

  openAssign = (showModal: () => void, coachUserId: string) => {
    this.assignCoachUserId = coachUserId;
    showModal();
  };

  <template>
    <div class="list-group-item" ...attributes>
      <div class="d-flex justify-content-between align-items-center gap-2">
        <span class="fw-medium">
          <Await @promise={{(this.loadCoachee)}}>
            <:pending>{{@plan.user}}</:pending>
            <:success as |coachee|>{{userDisplayName coachee}}</:success>
            <:error>{{@plan.user}}</:error>
          </Await>
        </span>
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary flex-shrink-0"
          aria-expanded="{{if this.expanded 'true' 'false'}}"
          {{on "click" this.toggle}}
        >
          {{if this.expanded "Hide" "Details"}}
        </button>
      </div>

      {{#if this.expanded}}
        <div class="mt-3">
          <Await @promise={{(this.loadPartnerships)}}>
            <:pending>
              <p class="text-muted small mb-0">Loading partnerships…</p>
            </:pending>
            <:success as |partnerships|>
              <ReassignCoachModal
                @planId={{this.planId}}
                @coaching={{@coaching}}
                @cohortId={{@cohortId}}
                @coacheeId={{@plan.user}}
                @partnershipId={{this.partnershipIdOf partnerships}}
                @currentCoachUserId={{this.currentCoachUserIdOf partnerships}}
                @currentCoachName={{this.currentCoachNameOf partnerships}}
                @initialCoachUserId={{this.assignCoachUserId}}
                @onSuccess={{this.reloadPartnerships}}
              >
                <:trigger as |showModal|>
                  {{#if partnerships.length}}
                    <div class="d-flex flex-column gap-2">
                      {{#each partnerships as |partnership|}}
                        <div class="border rounded p-2">
                          <div
                            class="d-flex justify-content-between align-items-center gap-2"
                          >
                            <span class="small">
                              Coach:
                              <span class="fw-medium">{{this.coachNameFor
                                  partnership.coachUserId
                                }}</span>
                            </span>
                            <span class="d-flex align-items-center gap-2">
                              <span
                                class="badge
                                  {{partnershipStatusClass partnership.status}}"
                              >{{partnership.status}}</span>
                              {{#if (eq partnership.status "chosen")}}
                                <IfAbility
                                  @ability="coaching-partnerships"
                                  @atLeast="editor"
                                >
                                  <:if>
                                    <button
                                      type="button"
                                      class="btn btn-sm btn-outline-primary"
                                      data-test-reassign-coach
                                      {{on
                                        "click"
                                        (fn this.openReassign showModal)
                                      }}
                                    >Reassign</button>
                                  </:if>
                                </IfAbility>
                              {{/if}}
                            </span>
                          </div>
                          <div class="mt-1">
                            <CoachingPartnershipSessions
                              @partnership={{partnership}}
                            />
                          </div>
                        </div>
                      {{/each}}
                    </div>
                  {{else}}
                    <p class="text-muted small mb-0">No coach partnerships yet.</p>
                  {{/if}}

                  <BestMatchedCoaches
                    @cohortId={{@cohortId}}
                    @coacheeId={{@plan.user}}
                    @currentCoachUserId={{this.currentCoachUserIdOf
                      partnerships
                    }}
                    @partnerships={{partnerships}}
                    @reloadToken={{this.reloadToken}}
                    @onAssign={{fn this.openAssign showModal}}
                  />
                </:trigger>
              </ReassignCoachModal>
            </:success>
            <:error>
              <p class="text-danger small mb-0">Couldn’t load partnerships.</p>
            </:error>
          </Await>
        </div>
      {{/if}}
    </div>
  </template>
}
